package io.github.retar.portfolio.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initAppsflyer_kmpPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("appsflyer_kmp.md", FrontMatterElement.Builder { addScalar("title", "Wrapping the AppsFlyer SDK into a Kotlin Multiplatform library (when no official KMP SDK exists)"); addScalar("description", "A coroutine-first wrapper around the AppsFlyer Android and iOS SDKs: all the logic in common code, two thin native adapters, deep links as a Flow, and 160 tests that never touch a real SDK."); addScalar("date", "2026-06-28"); addScalar("cover", "/articles/appsflyer_kmp.jpeg"); }))
}

@Page("/blog/appsflyer-kmp-wrapper")
@Layout(".pages.MarkdownPage")
@Composable
fun Appsflyer_kmpPage() {
    org.jetbrains.compose.web.dom.H1(attrs = { id("wrapping-the-appsflyer-sdk-into-a-kotlin-multiplatform-library-when-no-official-kmp-sdk-exists") }) {
        org.jetbrains.compose.web.dom.Text("Wrapping the AppsFlyer SDK into a Kotlin Multiplatform library (when no official KMP SDK exists)")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("AppsFlyer does attribution and deep linking for mobile apps. It ships good native SDKs for Android and iOS. What it doesn't ship is a Kotlin Multiplatform SDK. In a KMP app, where networking, domain logic and analytics already live in shared code, that means writing the integration twice: once in Kotlin against the Android SDK, once against the iOS SDK, each with its own callbacks.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So I wrote ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/retro99/appsflyer-kmp") {
            io.github.retar.portfolio.components.widgets.InlineCode("""appsflyer-kmp""")
        }
        org.jetbrains.compose.web.dom.Text(", an unofficial, experimental wrapper with one Kotlin API on both platforms. Asynchronous calls are suspending functions, and deep links arrive as a ")
        io.github.retar.portfolio.components.widgets.InlineCode("""Flow""")
        org.jetbrains.compose.web.dom.Text(".")
    }
    com.varabyte.kobweb.compose.dom.GenericTag("blockquote") {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Disclaimer:")
            }
            org.jetbrains.compose.web.dom.Text(" This is a personal, unofficial project, not affiliated with or endorsed by AppsFlyer. \"AppsFlyer\" and the AppsFlyer SDK name are trademarks of their respective owners.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-problem-with-two-integrations") }) {
        org.jetbrains.compose.web.dom.Text("The problem with two integrations")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The native SDKs are callback-based, and they don't even agree on shape. Android takes a listener at init:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""AppsFlyerLib.getInstance().init(devKey, conversionDataListener, context)
AppsFlyerLib.getInstance().start(application)
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("iOS uses delegate properties on a shared instance, and its deep links come through a second delegate:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""AppsFlyerLib.shared().delegate = self
AppsFlyerLib.shared().deepLinkDelegate = self
AppsFlyerLib.shared().start()
""", lang = "swift")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Conversion data, deep link results, in-app event delivery and purchase validation all come back through callbacks, and none of them compose with coroutines. Parameter names, delegate patterns and which features exist differ between the two platforms. Every app that wants this in shared code ends up writing the same adapter.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-shape-all-the-logic-in-common-code") }) {
        org.jetbrains.compose.web.dom.Text("The shape: all the logic in common code")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The obvious KMP structure is an ")
        io.github.retar.portfolio.components.widgets.InlineCode("""expect""")
        org.jetbrains.compose.web.dom.Text(" declaration for every SDK call, with an ")
        io.github.retar.portfolio.components.widgets.InlineCode("""actual""")
        org.jetbrains.compose.web.dom.Text(" on each platform. I went a different way. There is exactly one ")
        io.github.retar.portfolio.components.widgets.InlineCode("""expect""")
        org.jetbrains.compose.web.dom.Text(" in the library, a factory. Everything else in common code talks to an internal interface that describes the native SDK:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""internal interface AppsFlyerSdk {
    fun configure(
        config: AppsFlyerConfig,
        onConversion: (Map<String, Any?>) -> Unit,
        onConversionError: (String) -> Unit,
        onDeepLink: (DeepLinkResult) -> Unit,
        ...
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Android and iOS each implement that interface as a thin adapter: translate arguments, call the native SDK, hand results back as plain Kotlin values. All the behaviour (turning callbacks into suspending functions, idempotent start, dropping null parameters, mapping attribution payloads into typed results) lives once, in ")
        io.github.retar.portfolio.components.widgets.InlineCode("""commonMain""")
        org.jetbrains.compose.web.dom.Text(", in a single client class.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The payoff is testing. Because the platform SDK is just an interface, the tests use a ")
        io.github.retar.portfolio.components.widgets.InlineCode("""FakeAppsFlyerSdk""")
        org.jetbrains.compose.web.dom.Text(" and run in common code. There are about 160 of them, and none needs a device, an emulator or an AppsFlyer account. A small JVM target exists only so the code that converts Android's ")
        io.github.retar.portfolio.components.widgets.InlineCode("""org.json""")
        org.jetbrains.compose.web.dom.Text(" payloads into Kotlin maps can be unit tested too.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("callbacks-become-suspending-functions") }) {
        org.jetbrains.compose.web.dom.Text("Callbacks become suspending functions")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("One-shot callbacks, like \"was this event delivered?\", turn into a suspending call with ")
        io.github.retar.portfolio.components.widgets.InlineCode("""suspendCancellableCoroutine""")
        org.jetbrains.compose.web.dom.Text(":")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""override suspend fun logEventForResult(
    name: String,
    params: Map<String, Any?>,
): LogEventResult = suspendCancellableCoroutine { continuation ->
    sdk.logEventForResult(
        name = name,
        params = params.filterValues { value -> value != null },
    ) { result ->
        continuation.resume(result)
    }
}
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Conversion data and the start result are different: they arrive once, at some point after ")
        io.github.retar.portfolio.components.widgets.InlineCode("""start()""")
        org.jetbrains.compose.web.dom.Text(", and the app may ask for them before or after that happens. Those go into a ")
        io.github.retar.portfolio.components.widgets.InlineCode("""MutableSharedFlow""")
        org.jetbrains.compose.web.dom.Text(" with ")
        io.github.retar.portfolio.components.widgets.InlineCode("""replay = 1""")
        org.jetbrains.compose.web.dom.Text(", and the public function is just ")
        io.github.retar.portfolio.components.widgets.InlineCode("""first()""")
        org.jetbrains.compose.web.dom.Text(":")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""override suspend fun getConversionData(): CampaignData = conversionRelay.first()
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Called early, it suspends until the SDK answers. Called late, it returns the answer immediately. Callers don't have to know which.")
    }
    org.jetbrains.compose.web.dom.P {
        io.github.retar.portfolio.components.widgets.InlineCode("""start()""")
        org.jetbrains.compose.web.dom.Text(" is safe to call more than once. An atomic compare-and-exchange makes sure the native SDK is configured exactly once, however many places in an app call it.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("deep-links-as-a-flow") }) {
        org.jetbrains.compose.web.dom.Text("Deep links as a ")
        io.github.retar.portfolio.components.widgets.InlineCode("""Flow""")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Deep links are a stream: one on first launch after install (deferred), and more every time the app is opened from a link (direct). Both SDKs have a newer \"unified\" deep link API that reports both kinds through one callback, so the wrapper uses that: ")
        io.github.retar.portfolio.components.widgets.InlineCode("""subscribeForDeepLink""")
        org.jetbrains.compose.web.dom.Text(" on Android, ")
        io.github.retar.portfolio.components.widgets.InlineCode("""AppsFlyerDeepLinkDelegate""")
        org.jetbrains.compose.web.dom.Text(" on iOS. The older ")
        io.github.retar.portfolio.components.widgets.InlineCode("""onAppOpenAttribution""")
        org.jetbrains.compose.web.dom.Text(" callback is deliberately a no-op.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("In common code, the result is one sealed type:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""sealed interface DeepLinkResult {
    data class Found(
        val deepLinkValue: String?,
        val isDeferred: Boolean,
        val mediaSource: String?,
        val campaign: String?,
        val raw: Map<String, Any?>,
    ) : DeepLinkResult

    data object NotFound : DeepLinkResult

    data class Error(val message: String?) : DeepLinkResult
}
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The stream is a ")
        io.github.retar.portfolio.components.widgets.InlineCode("""MutableSharedFlow""")
        org.jetbrains.compose.web.dom.Text(" ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("without")
        }
        org.jetbrains.compose.web.dom.Text(" replay. That was a deliberate choice. A deep link is an instruction to navigate somewhere now. Replaying an old one to a screen that subscribes later would send people somewhere they didn't just ask to go. A test pins the behaviour down (")
        io.github.retar.portfolio.components.widgets.InlineCode("""deepLinkFlowDoesNotReplayPastEmissions""")
        org.jetbrains.compose.web.dom.Text(").")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The consequence is documented in the README: subscribe to ")
        io.github.retar.portfolio.components.widgets.InlineCode("""deepLink""")
        org.jetbrains.compose.web.dom.Text(" before calling ")
        io.github.retar.portfolio.components.widgets.InlineCode("""start()""")
        org.jetbrains.compose.web.dom.Text(", or the deferred deep link from the first launch can arrive before anyone is listening.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-ios-side-a-swift-bridge") }) {
        org.jetbrains.compose.web.dom.Text("The iOS side: a Swift bridge")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The iOS adapter doesn't implement the Objective-C delegates in Kotlin. It talks to a small Swift class compiled into the library. It implements both delegates and turns each callback into a closure that Kotlin passed in:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""public func configure(
    ...
    onConversion: @escaping (NSDictionary) -> Void,
    onConversionError: @escaping (String?) -> Void,
    onDeepLinkFound: @escaping (String?, Bool, String?, String?, NSDictionary?) -> Void,
    onDeepLinkNotFound: @escaping () -> Void,
    onDeepLinkError: @escaping (String?) -> Void,
    onStart: @escaping (Bool, Int, String?) -> Void
)
""", lang = "swift")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The bridge makes no decisions; it only translates. The iOS SDK itself comes in through Swift Package Manager, declared with the ")
        io.github.retar.portfolio.components.widgets.InlineCode("""spmForKmp""")
        org.jetbrains.compose.web.dom.Text(" Gradle plugin, so an app that uses the library doesn't add the package by hand. To call the API from Swift, the app exports the library from its own framework:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""kotlin {
    iosArm64().binaries.framework {
        export("org.retar.appsflyer:appsflyer:0.1.0")
    }
}
""", lang = "kotlin")
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("feature-parity-and-where-the-platforms-differ") }) {
        org.jetbrains.compose.web.dom.Text("Feature parity, and where the platforms differ")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The wrapper covers what the native SDKs do: install attribution and conversion data, OneLink deep links (deferred and direct), in-app events with delivery confirmation, ad revenue, purchase validation (VAL V2), uninstall measurement, and GDPR/DMA consent.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Some APIs exist on only one platform, and those are no-ops on the other: 12 iOS-only methods, such as ")
        io.github.retar.portfolio.components.widgets.InlineCode("""setDisableSKAdNetwork""")
        org.jetbrains.compose.web.dom.Text(", and 14 Android-only ones, such as ")
        io.github.retar.portfolio.components.widgets.InlineCode("""setCollectIMEI""")
        org.jetbrains.compose.web.dom.Text(". Each says so in its KDoc. A handful behave differently on each platform, for example log levels: Android has six, iOS only has debug on or off. Those are documented too, so nobody finds out in production. Android-only calls that need a ")
        io.github.retar.portfolio.components.widgets.InlineCode("""Context""")
        org.jetbrains.compose.web.dom.Text(", ")
        io.github.retar.portfolio.components.widgets.InlineCode("""Activity""")
        org.jetbrains.compose.web.dom.Text(" or ")
        io.github.retar.portfolio.components.widgets.InlineCode("""Intent""")
        org.jetbrains.compose.web.dom.Text(" are extension functions in ")
        io.github.retar.portfolio.components.widgets.InlineCode("""androidMain""")
        org.jetbrains.compose.web.dom.Text(":")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""fun AppsFlyerClient.performOnDeepLinking(intent: Intent, context: Context)
fun AppsFlyerClient.sendPushNotificationData(activity: Activity)
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("One normalisation applies everywhere: null values in event, ad revenue, partner data and purchase parameters are dropped before they reach the native SDK, because the two SDKs treat nulls differently.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-i-d-keep-from-this") }) {
        org.jetbrains.compose.web.dom.Text("What I'd keep from this")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Put the logic in common code and keep the platform code dumb. An ")
        io.github.retar.portfolio.components.widgets.InlineCode("""expect""")
        org.jetbrains.compose.web.dom.Text("/")
        io.github.retar.portfolio.components.widgets.InlineCode("""actual""")
        org.jetbrains.compose.web.dom.Text(" pair per SDK call spreads behaviour across three source sets. One interface over the native SDK keeps it in one place, where it can be tested with a fake.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Pick the flow type by what the event means. Data that arrives once and stays true can be replayed. A navigation instruction can't.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("try-it") }) {
        org.jetbrains.compose.web.dom.Text("Try it")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The library is on GitHub: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/retro99/appsflyer-kmp") {
            org.jetbrains.compose.web.dom.Text("retro99/appsflyer-kmp")
        }
        org.jetbrains.compose.web.dom.Text(". There's a Compose Multiplatform demo app in ")
        io.github.retar.portfolio.components.widgets.InlineCode("""samples/demo-app""")
        org.jetbrains.compose.web.dom.Text(" that exercises the API on Android and iOS.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("It's experimental and unofficial, and it isn't affiliated with AppsFlyer. Use it in production at your own risk.")
    }
}
